import { GitMerge } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { Id, Landing } from '@orgus/protocol';
import { Button, Dialog, Input, Select, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useCheckouts, useProjects } from '../../lib/store.ts';
import { submitLanding } from './api.ts';

const OTHER = '__other__';

/**
 * Put a branch in a landing queue: project, branch (from the checkouts Orgus sees, or typed),
 * target branch and an optional title.
 */
export function SubmitDialog({ open, onOpenChange, projectId: initialProject, target: initialTarget, onSubmitted }: { open: boolean; onOpenChange: (v: boolean) => void; projectId?: Id; target?: string; onSubmitted: (l: Landing) => void }) {
  const projects = useProjects();
  const [projectId, setProjectId] = useState<Id>('');
  const [branch, setBranch] = useState('');
  const [typed, setTyped] = useState('');
  const [target, setTarget] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const project = projects.find((p) => p.id === projectId);
  const checkouts = useCheckouts(projectId ? { projectId } : {});

  useEffect(() => {
    if (!open) return;
    const p = projects.find((x) => x.id === initialProject) ?? projects[0];
    setProjectId(p?.id ?? '');
    setTarget(initialTarget ?? p?.defaultBranch ?? 'main');
    setBranch('');
    setTyped('');
    setTitle('');
    setError(null);
    // Only when it opens.
  }, [open]);

  const branches = useMemo(() => {
    const seen = new Map<string, Id>();
    for (const c of checkouts) if (c.branch && c.branch !== (project?.defaultBranch ?? target) && !seen.has(c.branch)) seen.set(c.branch, c.id);
    return [...seen.entries()].map(([b, id]) => ({ branch: b, checkoutId: id }));
  }, [checkouts, project, target]);

  // With no other branch checked out anywhere, typing is the only way: open the field at once.
  useEffect(() => {
    if (open && !branch && !branches.length) setBranch(OTHER);
  }, [open, branch, branches.length]);

  const chosen = branch === OTHER ? typed.trim() : branch;
  const checkoutId = branches.find((b) => b.branch === chosen)?.checkoutId;
  const invalid = !projectId ? 'Pick a project.' : !chosen ? 'Pick or type the branch with the change.' : chosen === target ? 'That is the target branch itself.' : null;

  const submit = async () => {
    if (invalid || !project) return;
    setBusy(true);
    setError(null);
    try {
      const l = await submitLanding({ projectId, branch: chosen, target: target.trim() || project.defaultBranch, ...(title.trim() ? { title: title.trim() } : {}), ...(checkoutId ? { checkoutId } : {}) }, project.defaultBranch);
      onSubmitted(l);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={!busy}
      title="Submit a change to land"
      description="The queue rebases, verifies, pushes and deploys it."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<GitMerge />} loading={busy} disabled={!!invalid} onClick={submit}>
            Add to queue
          </Button>
        </>
      }
    >
      <form
        onKeyDown={(e) => {
          // Enter in a text field sends the form, as a submit button would.
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
            e.preventDefault();
            e.currentTarget.requestSubmit();
          }
        }}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Select
          label="Project"
          value={projectId}
          placeholder={projects.length ? 'Pick a project' : 'No projects yet'}
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
          onValueChange={(v) => {
            setProjectId(v);
            setBranch('');
            setTarget(projects.find((p) => p.id === v)?.defaultBranch ?? 'main');
          }}
        />
        <Select
          label="Branch with the change"
          value={branch}
          placeholder="Pick a branch"
          hint={branches.length ? 'Branches checked out on your machines. Local branches are read from their checkout; nothing is pushed for you.' : 'No other branch is checked out. Type the branch name.'}
          options={[...branches.map((b) => ({ value: b.branch, label: b.branch })), { value: OTHER, label: 'Another branch...' }]}
          onValueChange={setBranch}
        />
        {branch === OTHER && <Input label="Branch name" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="feat/my-change" spellCheck={false} />}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Target branch" value={target} onChange={(e) => setTarget(e.target.value)} spellCheck={false} hint="One queue per project and target." />
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={chosen || 'What it changes'} hint="Defaults to the branch." />
        </div>
        {(error || (invalid && chosen)) && (
          <p role="alert" className="text-ui text-status-danger">
            {error ? `${error.what} ${error.why} ${error.next}` : invalid}
          </p>
        )}
      </form>
    </Dialog>
  );
}
